import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Shield, CheckCircle, Loader2, Send,
  Sparkles, Leaf, Waves, Wind, Wrench, MapPin, Calendar, Image, Upload
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  cat_diarista: { icon: Sparkles, name: "Diarista" },
  cat_jardinagem: { icon: Leaf, name: "Jardinagem" },
  cat_piscina: { icon: Waves, name: "Piscina" },
  cat_arcondicionado: { icon: Wind, name: "Ar-Condicionado" },
  cat_reforma: { icon: Wrench, name: "Reforma Leve" }
};

export default function DirectServicePage() {
  const { providerId, categoryId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [category, setCategory] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    address: {
      street: "",
      number: "",
      neighborhood: "",
      city: "",
      state: "SP",
      zip: ""
    },
    preferred_dates: [],
    estimated_value: ""
  });

  useEffect(() => {
    fetchData();
  }, [providerId, categoryId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch provider
      const providerRes = await fetch(`${API_URL}/api/providers/${providerId}/status`, { headers });
      if (providerRes.ok) {
        setProvider(await providerRes.json());
      }

      // Fetch category
      const categoryRes = await fetch(`${API_URL}/api/categories/${categoryId}`);
      if (categoryRes.ok) {
        const cat = await categoryRes.json();
        setCategory(cat);
        setFormData(prev => ({
          ...prev,
          title: `Serviço de ${cat.name}`
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = async (value) => {
    if (!value || value <= 0) return;

    try {
      const response = await fetch(`${API_URL}/api/fees/calculate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category_id: categoryId,
          total_value: parseFloat(value),
          service_origin: "invited_provider",
          provider_status: provider?.status || "active"
        })
      });

      if (response.ok) {
        setFeeInfo(await response.json());
      }
    } catch (error) {
      console.error("Error calculating fee:", error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.address.city) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/services/direct`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          provider_id: providerId,
          category_id: categoryId,
          title: formData.title,
          description: formData.description,
          address: formData.address,
          preferred_dates: formData.preferred_dates,
          photos: [],
          service_origin: "invited_provider"
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success("Solicitação criada! O prestador será notificado.");
        navigate(`/diagnostic/${categoryId}?request_id=${result.request_id}&direct=true`);
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Erro ao criar solicitação");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const config = CATEGORY_CONFIG[categoryId] || { icon: Sparkles, name: "Serviço" };
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="direct-service-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-slate-600" />
                <span className="font-semibold text-slate-900">Criar Serviço Protegido</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Provider & Category Info */}
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <Icon className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{config.name}</p>
                  <p className="text-sm text-slate-600">Prestador: {provider?.name || "Convidado"}</p>
                </div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">
                <Shield className="w-3 h-3 mr-1" />
                Protegido eZelo.
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Service Form */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhes do Serviço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Título da Solicitação *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Ex: Limpeza completa apartamento"
                data-testid="title-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Descrição Detalhada *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descreva o serviço que você precisa..."
                rows={4}
                data-testid="description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Cidade *</Label>
                <Input
                  id="city"
                  value={formData.address.city}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, city: e.target.value}
                  })}
                  placeholder="São Paulo"
                  data-testid="city-input"
                />
              </div>
              <div>
                <Label htmlFor="neighborhood">Bairro</Label>
                <Input
                  id="neighborhood"
                  value={formData.address.neighborhood}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, neighborhood: e.target.value}
                  })}
                  placeholder="Jardins"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="street">Rua</Label>
                <Input
                  id="street"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, street: e.target.value}
                  })}
                  placeholder="Av. Paulista"
                />
              </div>
              <div>
                <Label htmlFor="number">Número</Label>
                <Input
                  id="number"
                  value={formData.address.number}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, number: e.target.value}
                  })}
                  placeholder="1000"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="estimated_value">Valor Estimado (R$)</Label>
              <Input
                id="estimated_value"
                type="number"
                value={formData.estimated_value}
                onChange={(e) => {
                  setFormData({...formData, estimated_value: e.target.value});
                  calculateFee(e.target.value);
                }}
                placeholder="0,00"
              />
            </div>

            {/* Fee Info */}
            {feeInfo && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p className="text-sm font-medium text-slate-700">Taxas da Plataforma:</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Taxa ({(feeInfo.fee_percentage * 100).toFixed(0)}%)</span>
                  <span className="font-medium">R$ {feeInfo.total_fee.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Pago por</span>
                  <Badge variant="outline">
                    {feeInfo.fee_payer === "client" ? "Cliente" : 
                     feeInfo.fee_payer === "provider" ? "Prestador" : "Dividido"}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 italic">{feeInfo.justification}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Protection Info */}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4">Este serviço está protegido pelo eZelo.</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Contrato digital gerado</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Pagamento em escrow</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Auditoria por IA</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Disputa mediada</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Retenção de garantia</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Histórico registrado</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button 
          className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="submit-btn"
        >
          {submitting ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Send className="w-5 h-5 mr-2" />
          )}
          Criar Solicitação Protegida
        </Button>
      </main>
    </div>
  );
}
